// command-palette.jsx
// Global ⌘K / Ctrl+K palette. Open via the keystroke or window.openPalette().
// Lists Navigate / Projects / Actions matched against the query string.
// Loaded after every screen, before app.jsx in index.html.

const { useState: useStateK, useEffect: useEffectK, useMemo: useMemoK, useRef: useRefK } = React;

const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
const META_LABEL = isMac ? "⌘" : "Ctrl";

// Same focus-trap selector as ModalShell (P72) — keep them aligned so
// future opt-out attributes work everywhere.
const _CP_FOCUSABLE_SEL = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].map((s) => `${s}:not([data-focus-skip])`).join(",");

function CommandPalette() {
  // P385 — dialog chrome localized via useT (group names + command
  // labels themselves still English in this iteration).
  const { t } = window.useT();
  const [open, setOpen] = useStateK(false);
  const [query, setQuery] = useStateK("");
  const [selected, setSelected] = useStateK(0);
  const inputRef = useRefK(null);
  const listRef = useRefK(null);
  const shellRef = useRefK(null);
  const auth = window.useAuth();
  // P293 — was `window.useProjects ? window.useProjects() : []`, a
  // conditional hook call. `useProjects` is a real hook (useState +
  // useEffect inside), so the count differed across renders if
  // `window.useProjects` was ever undefined → "Rendered more hooks
  // than during the previous render". The ternary was defending
  // against that exact undefined case but in practice shared.js
  // loads BEFORE command-palette.js per index.html script order, so
  // window.useProjects is always defined by the time this component
  // first renders. Safe to call directly. Same fix as P292's auth
  // hoist in modals.jsx.
  const projects = window.useProjects();

  // Global keystroke + window.openPalette().
  useEffectK(() => {
    window.openPalette = () => setOpen(true);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (open && e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.openPalette = () => {};
    };
  }, [open]);

  // Reset state when (re)opening; focus input. Capture the previously
  // focused element so we can restore focus when the palette closes
  // (parallel to ModalShell's P72 trap).
  useEffectK(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    setQuery("");
    setSelected(0);
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => {
      clearTimeout(t);
      if (previousFocus && typeof previousFocus.focus === "function") {
        try { previousFocus.focus(); } catch { /* tolerate detached */ }
      }
    };
  }, [open]);

  // Focus trap on Tab/Shift+Tab so keyboard navigation doesn't escape
  // to the underlying page while the palette is open.
  useEffectK(() => {
    if (!open) return;
    const onTrap = (e) => {
      if (e.key !== "Tab") return;
      const root = shellRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(_CP_FOCUSABLE_SEL);
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onTrap);
    return () => window.removeEventListener("keydown", onTrap);
  }, [open]);

  // Build the full command list. Each command:
  //   { id, group, label, hint?, kbd?, run() }
  const commands = useMemoK(() => {
    // P386 — group names + command labels + hints + toasts localized.
    // The stable command `id` is the dispatcher key (used in
    // suggestion-chip-style bespoke handling at line 540 area in
    // builder.jsx); labels flow through useT.
    const G_NAV = t("cp.groupNavigate");
    const G_PROJ = t("cp.groupProjects");
    const G_ACT = t("cp.groupActions");
    const cmds = [];

    // Navigate
    cmds.push(
      { id: "nav-landing", group: G_NAV, label: t("cp.navLanding"), icon: "◆", run: () => window.goTo("landing") },
      { id: "nav-pricing", group: G_NAV, label: t("cp.navPricing"), icon: "$", run: () => window.goTo("pricing") },
      { id: "nav-templates", group: G_NAV, label: t("cp.navTemplates"), icon: "▤", run: () => window.goTo("templates") },
    );
    if (auth.isAuthed) {
      cmds.push({ id: "nav-dashboard", group: G_NAV, label: t("cp.navDashboard"), icon: "▦", hint: auth.name, run: () => window.goTo("dashboard") });
    }
    if (auth.role === "operator") {
      cmds.push({ id: "nav-operator", group: G_NAV, label: t("cp.navOperator"), icon: "⚙", hint: "OPERATOR", run: () => window.goTo("operator") });
    }

    // Projects
    if (auth.isAuthed) {
      projects.slice(0, 12).forEach((p) => {
        // P358 — drop the URL portion when liveUrl is missing (status=live
        // can briefly be set without a URL). P386 — status names reuse
        // the `proj.statusLive` / `proj.statusDraft` keys so the palette
        // hint matches the dashboard project card pill.
        const hint = p.status === "live"
          ? (p.liveUrl ? `${t("proj.statusLive")} · ${p.liveUrl}` : t("proj.statusLive"))
          : `${t("proj.statusDraft")} · ${(p.tag || "new").toLowerCase()}`;
        cmds.push({
          id: `proj-${p.id}`,
          group: G_PROJ,
          label: p.name,
          hint,
          icon: "▣",
          swatch: p.swatch,
          ink: p.ink,
          run: () => window.goTo("builder", { project: p }),
        });
      });
    }

    // Actions
    if (!auth.isAuthed) {
      cmds.push(
        { id: "act-signin", group: G_ACT, label: t("cp.actSignin"), icon: "→", run: () => window.openAuth("sign-in") },
        { id: "act-signup", group: G_ACT, label: t("cp.actSignup"), icon: "+", run: () => window.openAuth("sign-up") },
        { id: "act-operator", group: G_ACT, label: t("cp.actSigninOperator"), icon: "⚙", hint: t("cp.hintDemoOnly"), run: () => window.signInAsOperator() },
      );
    } else {
      cmds.push(
        { id: "act-newproj", group: G_ACT, label: t("cp.actNewProject"), icon: "+", kbd: `${META_LABEL}N`, run: () => window.goTo("dashboard") },
        { id: "act-signout", group: G_ACT, label: t("cp.actSignout"), icon: "↩", hint: auth.email, run: () => window.signOut() },
      );
      if (auth.role !== "operator") {
        cmds.push({ id: "act-toperator", group: G_ACT, label: t("cp.actSwitchOperator"), icon: "⚙", hint: t("cp.hintDemo"), run: () => { window.signOut(); setTimeout(() => window.signInAsOperator(), 50); } });
      }
    }
    cmds.push(
      { id: "act-tweaks", group: G_ACT, label: t("cp.actTweaks"), icon: "✎", hint: t("cp.hintTweaks"), run: () => window.toast(t("cp.toastTweaks")) },
      { id: "act-feedback", group: G_ACT, label: t("cp.actFeedback"), icon: "✉", run: () => window.toast(t("cp.toastFeedback")) },
    );

    return cmds;
  }, [auth, projects, t]);

  const filtered = useMemoK(() => {
    if (!query.trim()) return commands;
    // P574 — diacritic + apostrophe normalization (P575: extracted
    // to shared window.searchNorm).
    const q = window.searchNorm(query);
    return commands.filter((c) =>
      window.searchNorm(c.label).includes(q) ||
      (c.hint && window.searchNorm(c.hint).includes(q)) ||
      window.searchNorm(c.group).includes(q),
    );
  }, [commands, query]);

  // Group filtered commands in original order while preserving group headers.
  const grouped = useMemoK(() => {
    const map = new Map();
    filtered.forEach((c) => {
      if (!map.has(c.group)) map.set(c.group, []);
      map.get(c.group).push(c);
    });
    return [...map.entries()];
  }, [filtered]);

  // Clamp selection within filtered list.
  useEffectK(() => {
    if (selected >= filtered.length) setSelected(Math.max(0, filtered.length - 1));
  }, [filtered.length]);

  const fire = (cmd) => {
    setOpen(false);
    setTimeout(() => cmd.run(), 0);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === "Home") {
      // P234 — ARIA APG combobox spec calls for Home to jump to the
      // first item. VS Code / Spotlight / every other command palette
      // honours this; keyboard power-users were tabbing through ↑ to
      // get back to the top because the palette didn't.
      e.preventDefault();
      if (filtered.length > 0) setSelected(0);
    } else if (e.key === "End") {
      // P234 — Same APG rule for End → last item. Useful for the long
      // help / project-list groups that spill past the visible area.
      e.preventDefault();
      if (filtered.length > 0) setSelected(filtered.length - 1);
    } else if (e.key === "Enter" && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      // P545 — IME guard. Same shape as P544 fixed for the chat /
      // dashboard / landing textareas. The palette search box can
      // be typed into in any locale; pre-fix Enter unconditionally
      // fired the first command + closed the palette, which dropped
      // an IME-composition mid-character (Japanese / Chinese /
      // Korean) and accidentally executed an unintended action.
      e.preventDefault();
      const cmd = filtered[selected];
      if (cmd) fire(cmd);
    }
  };

  // Keep selected row visible.
  useEffectK(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-cmd-i="${selected}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [selected, open]);

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div style={cpStyles.scrim} onClick={() => setOpen(false)}>
      <div
        ref={shellRef}
        style={cpStyles.shell}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("cp.dialogAria")}
        tabIndex={-1}
      >
        {/* P178 — Combobox + listbox pattern. Without aria-controls +
            aria-activedescendant, screen readers don't follow the
            keyboard-driven selection that sighted users see. */}
        <div style={cpStyles.searchRow}>
          <span style={cpStyles.searchIcon} aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={onKeyDown}
            placeholder={t("cp.searchPlaceholder")}
            aria-label={t("cp.searchAria")}
            role="combobox"
            aria-expanded={grouped.length > 0}
            aria-controls="cp-list"
            aria-activedescendant={grouped.length > 0 ? `cp-row-${selected}` : undefined}
            aria-autocomplete="list"
            style={cpStyles.search}
            spellCheck={false}
            // P602 — full mobile-keyboard hygiene; command-palette
            // queries are matched verbatim against command labels +
            // project names. Auto-cap on the first letter ("d" → "D")
            // would defeat searchNorm's lowercase normalization on the
            // first keystroke, and autocorrect could replace short
            // partial queries with unrelated dictionary words.
            autoCapitalize="none"
            autoCorrect="off"
            // P593 — Enter on the palette commits the highlighted
            // command (P545 IME-guarded); on mobile, "Go" / "Send"
            // is the right cue. enterKeyHint="go" is the most
            // accurate semantic match — mobile users see "Go"
            // instead of generic "Enter".
            enterKeyHint="go"
          />
          <span style={cpStyles.escKbd} aria-hidden="true">esc</span>
        </div>
        <div ref={listRef} style={cpStyles.list} id="cp-list" role="listbox" aria-label={t("cp.listAria")}>
          {grouped.length === 0 && (
            <div style={cpStyles.empty}>
              {t("cp.noMatches")}<b style={{ color: "var(--ink)" }}>"{query}"</b>
            </div>
          )}
          {grouped.map(([group, items]) => (
            <div key={group} style={cpStyles.group} role="group" aria-label={group}>
              <div style={cpStyles.groupHead} aria-hidden="true">{group}</div>
              {items.map((cmd) => {
                runningIndex += 1;
                const i = runningIndex;
                const isActive = i === selected;
                return (
                  <button
                    type="button"
                    key={cmd.id}
                    id={`cp-row-${i}`}
                    data-cmd-i={i}
                    style={{ ...cpStyles.row, ...(isActive ? cpStyles.rowActive : {}) }}
                    onMouseEnter={() => setSelected(i)}
                    onClick={() => fire(cmd)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span style={{
                      ...cpStyles.rowIcon,
                      ...(cmd.swatch ? { background: cmd.swatch, color: cmd.ink, fontFamily: "var(--font-display)", fontStyle: "italic" } : {}),
                    }} aria-hidden="true">{cmd.icon}</span>
                    <span style={cpStyles.rowLabel}>{cmd.label}</span>
                    {cmd.hint && <span style={cpStyles.rowHint}>{cmd.hint}</span>}
                    {cmd.kbd && <span style={cpStyles.rowKbd} aria-hidden="true">{cmd.kbd}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div style={cpStyles.footer}>
          {/* P467 — kbd glyph spans aria-hidden so SR reads the localized
              verb labels only (otherwise it announces "up arrow down arrow
              navigate"). Visible UI for sighted users unchanged. */}
          <span style={cpStyles.foot}>
            <span style={cpStyles.kbd} aria-hidden="true">↑</span><span style={cpStyles.kbd} aria-hidden="true">↓</span>
            <span style={cpStyles.footLabel}>{t("cp.footNavigate")}</span>
          </span>
          <span style={cpStyles.foot}>
            <span style={cpStyles.kbd} aria-hidden="true">↵</span>
            <span style={cpStyles.footLabel}>{t("cp.footSelect")}</span>
          </span>
          <span style={cpStyles.foot}>
            <span style={cpStyles.kbd} aria-hidden="true">{META_LABEL}</span><span style={cpStyles.kbd} aria-hidden="true">K</span>
            <span style={cpStyles.footLabel}>{t("cp.footToggle")}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

const cpStyles = {
  scrim: {
    position: "fixed",
    inset: 0,
    background: "color-mix(in oklab, var(--ink) 35%, transparent)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    zIndex: 2147483641,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: "12vh",
    padding: "12vh 16px 16px",
    animation: "modal-scrim 180ms ease-out both",
  },
  shell: {
    width: "min(640px, 100%)",
    maxHeight: "70vh",
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    boxShadow: "0 40px 100px -30px rgba(0,0,0,0.45)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "var(--font-ui)",
    color: "var(--ink)",
    animation: "modal-pop 200ms cubic-bezier(.2,.9,.3,1) both",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderBottom: "1px solid var(--rule)",
  },
  searchIcon: { color: "var(--ink-3)", fontSize: 18 },
  search: {
    flex: 1,
    border: "none",
    background: "transparent",
    color: "var(--ink)",
    fontSize: 16,
    outline: "none",
    fontFamily: "var(--font-ui)",
  },
  escKbd: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-3)",
    padding: "3px 7px",
    border: "1px solid var(--rule)",
    borderRadius: 4,
  },
  list: {
    overflowY: "auto",
    flex: 1,
    padding: "6px 0",
    minHeight: 80,
  },
  empty: {
    padding: "32px 18px",
    color: "var(--ink-2)",
    fontSize: 13,
    textAlign: "center",
  },
  group: { padding: "6px 0" },
  groupHead: {
    padding: "6px 16px 4px",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    letterSpacing: "0.1em",
    color: "var(--ink-3)",
    textTransform: "uppercase",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "8px 14px",
    background: "transparent",
    border: 0,
    color: "var(--ink)",
    fontFamily: "var(--font-ui)",
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left",
    borderLeft: "2px solid transparent",
  },
  rowActive: {
    background: "color-mix(in oklab, var(--accent) 10%, var(--paper))",
    borderLeftColor: "var(--accent)",
  },
  rowIcon: {
    width: 26,
    height: 26,
    borderRadius: 6,
    background: "var(--paper-2)",
    color: "var(--ink-2)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    flexShrink: 0,
  },
  rowLabel: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowHint: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--ink-3)",
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowKbd: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-3)",
    padding: "2px 6px",
    border: "1px solid var(--rule)",
    borderRadius: 4,
  },
  footer: {
    display: "flex",
    gap: 18,
    padding: "10px 16px",
    borderTop: "1px solid var(--rule)",
    background: "var(--paper-2)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--ink-3)",
  },
  foot: { display: "inline-flex", alignItems: "center", gap: 6 },
  kbd: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    border: "1px solid var(--rule)",
    background: "var(--paper)",
    borderRadius: 4,
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-2)",
  },
  footLabel: {},
};

window.CommandPalette = CommandPalette;
